import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Award, CheckCircle, Copy, ShieldCheck, Ticket } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Checkbox } from '../components/ui/Checkbox';
import { Badge } from '../components/ui/Badge';
import { useAuth } from '../contexts/AuthContext';
export function BenefitClaimPage() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const {
    user
  } = useAuth();
  const [agreed, setAgreed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    fullName: user?.nama || '',
    email: user?.email || '',
    phone: user?.phone || '',
    notes: ''
  });
  // Mock benefit data
  const benefit = {
    id: Number(id) || 1,
    title: 'Diskon Pelatihan & Workshop',
    provider: 'ATIOS Academy',
    discount: '50%',
    validUntil: '31 Desember 2025'
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreed) {
      alert('Mohon setujui syarat dan ketentuan terlebih dahulu');
      return;
    }
    // Simulate API call
    setTimeout(() => {
      setSubmitted(true);
    }, 1000);
  };
  const handleCopyCode = () => {
    navigator.clipboard.writeText('ATIOS-LEARN-2025-XYZ');
    alert('Kode voucher berhasil disalin!');
  };
  if (submitted) {
    return <div className="min-h-screen bg-white flex items-center justify-center p-4" data-id="element-1053">
        <Card className="max-w-md w-full text-center" data-id="element-1054">
          <CardContent className="pt-8 pb-8 px-6" data-id="element-1055">
            <div className="w-16 h-16 bg-success-light rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1056">
              <CheckCircle className="w-8 h-8 text-success" data-id="element-1057" />
            </div>
            <h2 className="text-2xl font-bold text-neutral-900 mb-2" data-id="element-1058">
              Klaim Berhasil!
            </h2>
            <p className="text-neutral-600 mb-8" data-id="element-1059">
              Selamat! Anda telah berhasil mengklaim manfaat ini. Gunakan kode
              di bawah ini saat bertransaksi.
            </p>

            <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4 mb-8" data-id="element-1060">
              <p className="text-xs text-neutral-500 uppercase tracking-wider mb-2" data-id="element-1061">
                Kode Voucher Anda
              </p>
              <div className="flex items-center justify-center gap-3" data-id="element-1062">
                <span className="text-xl font-mono font-bold text-atios-primary" data-id="element-1063">
                  ATIOS-LEARN-2025-XYZ
                </span>
                <button onClick={handleCopyCode} className="text-neutral-400 hover:text-neutral-600 transition-colors" title="Salin Kode" data-id="element-1064">
                  <Copy className="w-5 h-5" data-id="element-1065" />
                </button>
              </div>
            </div>

            <div className="space-y-3" data-id="element-1066">
              <Button className="w-full" onClick={() => navigate('/benefits/history')} data-id="element-1067">
                Lihat Riwayat Klaim
              </Button>
              <Button variant="secondary" className="w-full" onClick={() => navigate('/benefits')} data-id="element-1068">
                Kembali ke Daftar Manfaat
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>;
  }
  return <div className="min-h-screen bg-white" data-id="element-1069">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-1070">
        {/* Header */}
        <div className="mb-8" data-id="element-1071">
          <Button variant="ghost" onClick={() => navigate(`/benefits/${id}`)} className="mb-4 pl-0 hover:pl-2 transition-all" data-id="element-1072">
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-1073" />
            Kembali ke Detail
          </Button>
          <h1 className="text-3xl font-semibold text-neutral-900 mb-2" data-id="element-1074">
            Klaim Manfaat
          </h1>
          <p className="text-neutral-600" data-id="element-1075">
            Lengkapi data di bawah untuk mendapatkan kode voucher Anda
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-1076">
          {/* Summary Sidebar */}
          <div className="lg:col-span-1" data-id="element-1077">
            <Card className="bg-neutral-50 border-neutral-200 sticky top-8" data-id="element-1078">
              <CardHeader data-id="element-1079">
                <CardTitle className="text-base" data-id="element-1080">Ringkasan Manfaat</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4" data-id="element-1081">
                <div data-id="element-1082">
                  <h3 className="font-semibold text-neutral-900 mb-1" data-id="element-1083">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-neutral-600" data-id="element-1084">{benefit.provider}</p>
                </div>

                <div className="py-4 border-y border-neutral-200" data-id="element-1085">
                  <div className="flex justify-between items-center mb-2" data-id="element-1086">
                    <span className="text-sm text-neutral-600" data-id="element-1087">Nilai</span>
                    <Badge variant="success" data-id="element-1088">{benefit.discount}</Badge>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-1089">
                    <span className="text-sm text-neutral-600" data-id="element-1090">Berlaku</span>
                    <span className="text-sm font-medium text-neutral-900" data-id="element-1091">
                      {benefit.validUntil}
                    </span>
                  </div>
                </div>

                <div className="flex items-start gap-2 text-xs text-neutral-500" data-id="element-1092">
                  <ShieldCheck className="w-4 h-4 flex-shrink-0 text-atios-primary" data-id="element-1093" />
                  <p data-id="element-1094">
                    Data Anda aman dan hanya digunakan untuk keperluan
                    verifikasi klaim ini.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Claim Form */}
          <div className="lg:col-span-2" data-id="element-1095">
            <form onSubmit={handleSubmit} data-id="element-1096">
              <Card data-id="element-1097">
                <CardHeader data-id="element-1098">
                  <CardTitle className="flex items-center gap-2" data-id="element-1099">
                    <Ticket className="w-5 h-5 text-atios-primary" data-id="element-1100" />
                    Formulir Klaim
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6" data-id="element-1101">
                  <Input label="Nama Lengkap" value={formData.fullName} onChange={e => setFormData({
                  ...formData,
                  fullName: e.target.value
                })} required placeholder="Nama sesuai KTP" data-id="element-1102" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1103">
                    <Input label="Email" type="email" value={formData.email} onChange={e => setFormData({
                    ...formData,
                    email: e.target.value
                  })} required placeholder="email@contoh.com" data-id="element-1104" />
                    <Input label="Nomor Telepon" type="tel" value={formData.phone} onChange={e => setFormData({
                    ...formData,
                    phone: e.target.value
                  })} required placeholder="081234567890" data-id="element-1105" />
                  </div>

                  <div data-id="element-1106">
                    <label className="text-sm font-medium text-neutral-700 block mb-1.5" data-id="element-1107">
                      Catatan Tambahan (Opsional)
                    </label>
                    <textarea className="w-full min-h-[100px] rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 placeholder:text-neutral-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-atios-primary focus-visible:ring-offset-0" placeholder="Tulis catatan jika ada..." value={formData.notes} onChange={e => setFormData({
                    ...formData,
                    notes: e.target.value
                  })} data-id="element-1108" />
                  </div>

                  <div className="pt-4 border-t border-neutral-200" data-id="element-1109">
                    <Checkbox checked={agreed} onChange={e => setAgreed(e.target.checked)} label={<span className="text-sm text-neutral-700" data-id="element-1111">
                          Saya menyetujui syarat dan ketentuan penggunaan
                          manfaat ini serta menjamin kebenaran data yang saya
                          berikan.
                        </span>} data-id="element-1110" />
                  </div>

                  <div className="flex gap-3 pt-2" data-id="element-1112">
                    <Button type="button" variant="secondary" className="flex-1" onClick={() => navigate(`/benefits/${id}`)} data-id="element-1113">
                      Batal
                    </Button>
                    <Button type="submit" className="flex-1" disabled={!agreed} data-id="element-1114">
                      <Award className="w-4 h-4 mr-2" data-id="element-1115" />
                      Klaim Sekarang
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </form>
          </div>
        </div>
      </div>
    </div>;
}